<template>
  <div class="application">
    <PropertyPhotoPreview :image-url="applicationBundle.property.coverImage" size="md" />

    <View direction="column" justify="space-between" gap="sm" :grow="1">
      <Text variant="h5">{{ applicationBundle.property.displayName }}</Text>
      <ApplicationBundleStatusBadge :status="applicationBundle.status" />
      <Text variant="label-small" color="secondary">{{ lastUpdated }}</Text>
    </View>

    <div class="actions">
      <Button
        v-if="canResendApplication"
        variant="secondary"
        size="medium"
        :loading="isResendingApplication"
        @click="resendApplication"
      >
        Resend application
      </Button>

      <Button
        :to="renterApplicationReviewRoute(propertyId, renterId, applicationBundle.id)"
        variant="primary"
        size="medium"
        v-else-if="isReadyForReview"
      >
        Review
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { ApplicationBundleStatus } from '@tallo/applications';
import { Button, Text, View, useAlertsStore } from '@tallo/design-system';
import { ApplicationBundleDto, ApplicationBundleStatusBadge } from '@tallo/investor/applications';
import { renterApplicationReviewRoute } from '@tallo/investor/navigation';
import { PropertyPhotoPreview } from '@tallo/property';

import { useRenterProfileStore } from '../store/renter-profile.store';

const props = defineProps<{ applicationBundle: ApplicationBundleDto }>();

const alertsStore = useAlertsStore();
const renterProfileStore = useRenterProfileStore();
const isResendingApplication = ref(false);
const canResendApplication = computed(() => props.applicationBundle.status === ApplicationBundleStatus.SENT);
const isReadyForReview = computed(() => {
  return [
    ApplicationBundleStatus.SUBMITTED,
    ApplicationBundleStatus.ACCEPTED,
    ApplicationBundleStatus.DECLINED,
  ].includes(props.applicationBundle.status);
});
const route = useRoute();
const renterId = ref(route.params.renterId as string);
const propertyId = ref(route.params.propertyId as string);
const lastUpdated = computed(() => `Last update ${useDateFormat(props.applicationBundle.updatedAt, 'MMM D').value}`);

async function resendApplication() {
  isResendingApplication.value = true;

  renterProfileStore
    .resendApplication(props.applicationBundle.id)
    .then(() => alertsStore.success('The application has been resent'))
    .catch(() => alertsStore.somethingWentWrong())
    .finally(() => (isResendingApplication.value = false));
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.application {
  display: grid;
  grid-template-columns: 5rem auto auto;
  gap: var(--t-spacing-md);

  @include screen-max-sm {
    grid-template-columns: 5rem auto;

    .actions {
      width: 100%;
      grid-column: 1 / 3;

      & > * {
        width: 100%;
      }
    }
  }

  .actions {
    justify-self: end;
  }
}
</style>
